import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconSettings } from 'src/assets/icons/IconSettings';
import { ButtonText } from 'src/components/ButtonText';
import { CUSTOM_ICON_BUTTON_SIZE, CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import Score, { getScoreProps, SCORE_SIZE } from 'src/components/Score';
import { DEFAULT_TAXONOMY_NAME } from 'src/data/common';
import { isNonEnglish } from 'src/data/generation';
import { formatNumber, formatPercentage } from 'src/lib/format';

import { Loader } from './components/Loader';
import { useStyles } from './useStyles';

const MINIMUM_BASED_ON_COUNT = 15;

export const ScoringMethod = ({
  className,
  score,
  basedOnValue,
  loading,
  blocked,
  locked,
  readOnly,
  scoringMethodName,
  onScoringMethodClick,
  displayScoringMethod,
  language,
}) => {
  const styles = useStyles();

  const displayedName = scoringMethodName || DEFAULT_TAXONOMY_NAME;
  const similarCopiesFormatted = formatNumber(blocked ? 0 : basedOnValue || 0);

  const { textScore } = getScoreProps(score);
  const isEnglish = !isNonEnglish(language);

  const basedOnLabel =
    basedOnValue >= MINIMUM_BASED_ON_COUNT
      ? `Better than ${formatPercentage(textScore)} of ${similarCopiesFormatted}`
      : `We didn't find enough`;

  if (loading) {
    return <Loader />;
  }

  const isClickable = onScoringMethodClick && !readOnly && !locked;

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.container}>
        <Score
          classes={{ score: styles.score }}
          score={score}
          size={SCORE_SIZE.xxxl}
          locked={locked}
          strokeWidth={4}
        />
        {displayScoringMethod && (
          <>
            <CustomTypography
              className={styles.title}
              variant={TYPOGRAPHY_VARIANT.medium16}
            >
              <span>{basedOnLabel} similar texts in Anyword&apos;s </span>
              {isEnglish ? (
                <CustomTooltip
                  title={
                    isClickable
                      ? 'Select the topic or industry most relevant to your product/content. This will help score your copy variations more accurately.'
                      : ''
                  }
                >
                  <ButtonText
                    className={styles.button}
                    onClick={!readOnly && !locked ? onScoringMethodClick : undefined}
                  >
                    {displayedName}
                  </ButtonText>
                </CustomTooltip>
              ) : (
                <span>General</span>
              )}
              <span> data set</span>
            </CustomTypography>
            {isClickable && isEnglish && (
              <CustomIconButton
                onClick={onScoringMethodClick}
                size={CUSTOM_ICON_BUTTON_SIZE.small}
                disabled={readOnly || locked}
              >
                <IconSettings />
              </CustomIconButton>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ScoringMethod.propTypes = {
  className: PropTypes.string,
  score: PropTypes.number,
  basedOnValue: PropTypes.number,
  loading: PropTypes.bool,
  blocked: PropTypes.bool,
  locked: PropTypes.bool,
  readOnly: PropTypes.bool,
  scoringMethodName: PropTypes.string,
  onScoringMethodClick: PropTypes.func,
  displayScoringMethod: PropTypes.bool,
  language: PropTypes.string,
};

ScoringMethod.defaultProps = {
  className: null,
  score: null,
  basedOnValue: null,
  loading: null,
  blocked: null,
  locked: null,
  readOnly: null,
  scoringMethodName: null,
  onScoringMethodClick: null,
  displayScoringMethod: false,
  language: null,
};
